import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { decode } from 'html-entities';
import { generateJSON } from '@tiptap/html';
import getEditorExtensions from '../utils/tiptap-extensions';
import { fixContentFormat } from '../utils/helpers';

export const BuilderSections = {
	Education: 'education',
	WorkExperience: 'work_experience',
	ProjectExperience: 'project_experience',
	LeadershipExperience: 'leadership_experience',
	CustomExperience: 'custom_experience',
};

// The id of the element wrapping one entry, which Resume Score cards highlight. Experience,
// education and custom entries come from different tables and can share an id, so each kind
// gets its own prefix (EN-1152); SuggestionBuilder points experience cards at `add-more-bp-`.
const ENTRY_ELEMENT_PREFIX = {
	[BuilderSections.Education]: 'education-entry',
	[BuilderSections.CustomExperience]: 'custom-entry',
};

export const entryElementId = (sectionType, itemId) => `${ENTRY_ELEMENT_PREFIX[sectionType] ?? 'add-more-bp'}-${itemId}`;

export const placeholderTypes = {
	[BuilderSections.Education]: { header: 'EDUCATION', title: 'University', description: 'Degree', achievements: 'GPA, honors, relevant coursework' }, 
	[BuilderSections.WorkExperience]: { header: 'WORK EXPERIENCE', title: 'Company', description: 'Job title' }, 
	[BuilderSections.ProjectExperience]: { header: 'PROJECT EXPERIENCE', title: 'Organization', description: 'Title' }, 
	[BuilderSections.LeadershipExperience]: { header: 'LEADERSHIP EXPERIENCE', title: 'Organization', description: 'Title', achievements: 'Responsibilities and impact' }, 
	[BuilderSections.CustomExperience]: { header: 'CUSTOM EXPERIENCE' }
};

export const defaultData = (isCoverLetter = false, defaultTemplate = 'basic') => {
	const body_section = ({ title = '<b>Untitled</b>', name = '', contact = '', template = '', options = [] } = {}) => ({ title, name, contact, template, options });

	// define custom items inside each section
	const education_item = () => {
		return { id: Date.now(), uuid: uuid(), heading: '', location: '', title: '', description: [], date: 'Date', date_hidden: false };
	};
	const experience_item = () => {
		return { id: Date.now(), uuid: uuid(), heading: '', location: '', title: '', other: '', points: null, bullet_points: null, start_at: 'Start Date', end_at: 'Finish Date', date_hidden: false };
	};
	const custom_item = () => {
		return { id: Date.now(), uuid: uuid(), title: null, value: null };
	};

	const build_section = ({ title, type, items, order = 0, options = { allowAddItems: true } } = {}) => ({ title, type, items, order, options });
	const repeat_item = (item, n_times) => Array.from({ length: n_times }, (v, i) => ({ ...item, ...{ order: i } }));

	const buildItems = () => {
		return experience_item();
	};

	const defaultDataByTemplates = {
		basic: {
			...body_section(), 
			...{ sections: [
				build_section({ title: '<b>Education</b>', type: BuilderSections.Education, items: (() => {
					return [buildItems()];
				})(), order: 0 }), 
				build_section({ title: '<b>Work Experience</b>', type: BuilderSections.WorkExperience, items: (() => {
					return [buildItems(), buildItems(), buildItems()];
				})(), order: 1 }), 
				build_section({ title: '<b>Project Experience</b>', type: BuilderSections.ProjectExperience, items: (() => {
					return [buildItems(), buildItems(), buildItems()];
				})(), order: 2 }), 
				build_section({ title: '<b>Leadership Experience</b>', type: BuilderSections.LeadershipExperience, items: (() => {
					return [buildItems(), buildItems(), buildItems()];
				})(), order: 3 }), 
				build_section({ title: '<b>Custom Section</b>', type: BuilderSections.CustomExperience, items: repeat_item(custom_item(), 1), options: { showTitle: true, allowAddItems: true }, order: 4 }), 
			] } },

		standart: {
			...body_section(), 
			...{ sections: [
				build_section({ title: '<b>Technologies</b>', type: BuilderSections.CustomExperience, items: repeat_item(custom_item, 1), options: { showTitle: true, allowAddItems: false }, order: 0 }), 
				build_section({ title: '<b>Education</b>', type: BuilderSections.Education, items: repeat_item(education_item, 1), order: 1 }), 
				build_section({ title: '<b>Relevant Experience</b>', type: BuilderSections.WorkExperience, items: repeat_item(experience_item, 2), order: 2 }), 
				build_section({ title: '<b>Projects</b>', type: BuilderSections.ProjectExperience, items: repeat_item(experience_item, 1), order: 3 }), 
				build_section({ title: '<b>Additional Experience</b>', type: BuilderSections.LeadershipExperience, items: repeat_item(experience_item, 3), order: 4 }), 
			] } },

		technical:  {
			...body_section(), 
			...{ sections: [
				build_section({ title: '<b>Education</b>', type: BuilderSections.Education, items: repeat_item(education_item, 1), order: 0 }), 
				build_section({ title: '<b>Technical Skills</b>', type: BuilderSections.CustomExperience, items: repeat_item(custom_item, 1), options: { showTitle: true, allowAddItems: true }, order: 1 }), 
				build_section({ title: '<b>Relevant Experience</b>', type: BuilderSections.WorkExperience, items: repeat_item(experience_item, 2), order: 2 }), 
				build_section({ title: '<b>Technical Projects</b>', type: BuilderSections.ProjectExperience, items: repeat_item(experience_item, 2), order: 3 }), 

			] } },
	};

	return isCoverLetter 
		? body_section()
		: defaultDataByTemplates?.[defaultTemplate] ?? defaultDataByTemplates['basic'];
};

export const getName = (user) => {
	const name = [user?.first_name, user?.last_name]
		.filter(Boolean)
		.join(' ');

	return name || user?.name || user?.email;
};

export const styleZoom = (defaultValue, zoom) => {
	return (defaultValue * zoom) / 100;
};

export const valueToPx = (value) => `${value}px`;

export const timer = (time = 50) => new Promise((res) => setTimeout(() => res(), time));

// EN-1071: an editor left empty still writes markup (`<p></p>`), and legacy rows hold `[]`.
export const hasVisibleText = (html) => (
	typeof html === 'string' && decode(html.replace(/<[^>]*>/g, '')).trim() !== ''
);

export const parseBuilderDate = (key, item, present = false) => {
	const value = item[key];

	if (['Date', 'Start Date', 'Finish Date', 'Present', null, undefined].includes(value)) {
		return present ? {
			[key]: null,
			is_present: value === 'Present'
		} : {
			[key]: null,
		};
	}

	const date = moment(value, 'MMM YYYY').format('MMM YYYY');

	return {
		[key]: date,
		is_present: false,
	};
};

export const Months = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

const getFormattedDate = (date) => {
	if (!date) {return;}
	return moment(date, 'YYYY-MM-DD').format('MMM YYYY');
};

export const parseDate = (item, key, label, keepPresent = true) => {
	if (keepPresent && item.is_present) {
		return 'Present';
	}

	return getFormattedDate(item[key]) || `${label || ''}Date`;
};

export const parseRetrievedResume = (data) => {
	const { title, template, name, contact, options, preview, ...rest } = data;

	return {
		...rest,
		title,
		name,
		contact,
		template,
		options,
		preview,
		sections: data.sections.map((section) => {
			switch (section.type) {
				case BuilderSections.Education:
					return {
						id: section.id,
						title: section.title,
						type: section.type,
						order: section.order,
						options: section.options,
						items: section.items.map((item) => ({
							id: item.id,
							uuid: item.uuid,
							parent_uuid: item.parent_uuid,
							heading: item.heading,
							location: item.location,
							title: item.title,
							description: item.description,
							order: item.order,
							meta: item.meta,
							only_year: item.only_year,
							date: parseDate(item, 'date'),
							date_hidden: item.date_hidden,
							is_present: item.is_present || false,
						})),
					};

				case BuilderSections.CustomExperience:
					return {
						id: section.id,
						title: section.title,
						type: section.type,
						order: section.order,
						options: section.options,
						items: section.items.map((item) => ({
							id: item.id,
							uuid: item.uuid,
							parent_id: item.parent_id,
							title: item.title,
							value: item.value,
							order: item.order,
							created_at: item.created_at,
							updated_at: item.updated_at,
						})),
					};

				default: {
					return {
						id: section.id,
						title: section.title,
						type: section.type,
						order: section.order,
						options: section.options,
						items: section.items.map((item) => ({
							id: item.id,
							uuid: item.uuid,
							parent_uuid: item.parent_uuid,
							heading: item.heading,
							location: item.location,
							title: item.title,
							other: item.other,
							order: item.order,
							points: item.points,
							bullet_points: item.bullet_points,
							meta: item.meta,
							only_year: item.only_year,
							start_at: parseDate(item, 'start_at', 'Start ', false),
							end_at: parseDate(item, 'end_at', 'Finish '),
							is_present: item.is_present || false,
							date_hidden: item.date_hidden,
						})),
					};
				}
			}
		})
	};
};

export const isValidDate = (date) => {
	return !['Date', 'Start Date', 'Finish Date', 'Present', null, undefined].includes(date);
};

export const getFormattedContact = (user) => {
	const createLinkedInLinkFrom = (linkedInUrl, userName) => {
		return `<a target="_blank" rel="noopener noreferrer nofollow" class="underline decoration-[#2A8176]" href="${linkedInUrl}">${`${userName}'s LinkedIn`}</a>`;
	};

	const location = user?.options?.location || 'Toronto, ON';
	const phoneNumber = user?.phone || 'Phone #';
	const email = user?.email || 'Email';
	const linkedIn = user?.user_information?.linkedIn ? createLinkedInLinkFrom(user.user_information.linkedIn, user.name) : 'LinkedIn Link';

	return `<p>${location} | ${phoneNumber} | ${email} | ${linkedIn}</p>`;
};

export const getPointsFrom = (node) => {
	if (node?.content == null && node?.text == null && !Array.isArray(node)) {
		return [];
	}
    
	if (node?.type === 'listItem') {
		const bpArr = getPointsFrom(node.content).flat() || '';
		
		return {
			id: node.attrs.id,
			bp: bpArr?.join('') || bpArr
		};
	} else if (Array.isArray(node)) {
		return node.map((item) => {
			return getPointsFrom(item);
		}).flat();
	}  else if (node?.text != null || node?.type === 'text') {
		return node.text;
	} else if (Array.isArray(node?.content)) {
		return node.content.map((child) => getPointsFrom(child));
	} 
	return getPointsFrom({ content: node });
	
};

export const getBulletPointsFrom = (bulletPointsStr) => {
	const bulletPoints = [];

	const jsonContent = generateJSON(
		fixContentFormat(bulletPointsStr) || '',
		getEditorExtensions(true, true, 'true', undefined)
	);
    
	jsonContent.content.forEach((item) => {
		if (item.type !== 'bulletList') {
			return;
		}

		if (Array.isArray(item.content)) {
			item.content.forEach((listItem) => {
				bulletPoints.push(getPointsFrom(listItem));
			});
		} else {
			bulletPoints.push(...getPointsFrom(item));
		}
	});

	return bulletPoints;
};
